import {
    Component,
    h,
    Host,
    Prop,
} from '@stencil/core';
import { ThemeType } from '@uipath/portal-shell-util';

import store from '../../store/store';
import { getTheme } from '../../utils/themeHelper';

@Component({
    tag: 'portal-button',
    styleUrl: 'portal-button.scss',
    shadow: true,
})
export class PortalButton {
    /**
 * 'disabled' property
 * @memberof PortalButton
 */

    @Prop() disabled: boolean;

    /**
     * 'label' property
     * @memberof PortalButton
     */

    @Prop() label: string;

    /**
     * 'type' property of the button
     * value : primary | secondary | text
     * Default is text
     * @memberof PortalButton
     */
    @Prop() type: 'primary' | 'text' | 'secondary' = 'text';

    /**
     * Theme override to bypass store state
     * @memberof PortalButton
     */
    @Prop() theme: ThemeType = 'autoTheme';

    /**
     * 'size' property of the button
     * value : 'xs' | 'sm' | 'md' | 'lg' | 'xl'
     * Default is `sm`
     * @memberof PortalButton
     */
    @Prop() size: 'xs' | 'sm' | 'md' | 'lg' | 'xl' = 'sm';

    /**
     * Pass the name of the icon if we want to show button with an icon
     * @memberof PortalButton
     */
    @Prop() icon: string | null = null;

    /**
     * 'iconDirection' property of the button
     * value : 'left' | 'right'
     * Default is `left`
     * @memberof PortalButton
     */
    @Prop() iconDirection: 'left' | 'right' = 'left';

    /**
     * `href` property in case the button is of type link
     * @memberof PortalButton
     */
    @Prop() href: string;

    /**
     * `cssClass` property of the button
     * @memberof PortalButton
     */
    @Prop() cssClass: string = '';

    /**
     * `haspopup` accessibility attribute. Pass this if the button will open a menu/popup
     * @memberof PortalButton
     */
    @Prop() haspopup: boolean = false;

    /**
     * `expanded` accessibility attribute
     * @memberof PortalButton
     */
    @Prop() expanded: boolean = false;

    /**
     * `controls` accessibility attribute
     * @memberof PortalButton
     */
    @Prop() controls: string;

    /**
     * `describedBy` accessibility attribute
     * @memberof PortalButton
     */
    @Prop() describedBy: string;

    /**
     * `target` property can be set when button is of type link. Either we want to open in a new tab or in the same tab.
     * @memberof PortalButton
     */
    @Prop() target: string;

    /**
     * `iconSize` will set the size of the icon. This will be used only when there is a button with icon
     * @memberof PortalButton
     */
    @Prop() iconSize: string;

    render() {
        const content = [
            this.icon && this.icon !== undefined && (
                <span>
                    <portal-icon size={this.iconSize} name={this.icon} class={{ 'button-icon': true }} />
                </span>
            ),
            <span
                class={{ 'button-text': true }}
            >
                {this.label}
            </span>,
        ];

        const classes = {
            'portal-button': true,
            [`icon-${this.iconDirection}`]: this.icon != null,
            [this.cssClass]: true,
            [this.size]: true,
            [this.type]: true,
        };

        const props = {
            'aria-haspopup': `${this.haspopup}`,
            'aria-expanded': this.controls && `${this.expanded}`,
            'aria-controls': this.controls,
            'aria-described-by': this.describedBy,
            disabled: this.disabled,
            type: this.type,
        };
        return (
            <Host class={getTheme(store, this.theme)}>
                {this.href ? (
                    <a class={classes} {...this.getAnchorProps()} href={this.href}>
                        {content}
                    </a>
                ) : (
                    <button {...props} class={classes}>
                        {content}
                    </button>
                )}
            </Host>
        );
    }

    private getAnchorProps() {
        const props = {};

        this.href && this.target && (props['target'] = this.target);
        this.target && this.href && (props['rel'] = 'noreferrer noopener');

        return props;
    }
}
